import React, { useState, useMemo, useEffect } from "react";
import { RefreshCw, Calendar, Users, FileText, BarChart3, TrendingUp, Target, PieChart, Loader2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import api from "../api/client";


const typeMultipliers = {
  "CashSale": -1,
  "CustInvc": -1,
  "CustCred": 1,
  "CashRefund": 1,
};

const getMonthsInRange = (start, end) => {
  const result = [];
  const s = new Date(start), e = new Date(end);
  const d = new Date(s.getFullYear(), s.getMonth(), 1);

  while (d <= e) {
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() + 1);
  }
  return result;
};

const AnalyticsDashboard = () => {
  // API data state
  const [apiData, setApiData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [startPicker, setStartPicker] = useState("2024-01-01");
  const [endPicker, setEndPicker] = useState("2024-12-31");
  const [repPicker, setRepPicker] = useState("ALL");
  const [typePicker, setTypePicker] = useState("ALL");

  const [activeStart, setActiveStart] = useState(startPicker);
  const [activeEnd, setActiveEnd] = useState(endPicker);
  const [activeRep, setActiveRep] = useState(repPicker);
  const [activeType, setActiveType] = useState(typePicker);

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch transaction data
        const transactionResponse = await api.get('/api/getJsonFromS32024');
        const transactionJson = transactionResponse.data;
        const transactionArray = Array.isArray(transactionJson)
          ? transactionJson
          : (transactionJson.rows || []);

        // Fetch budget data
        const budgetResponse = await api.get('/api/getCombinedBudgetS32024');
        const budgetJson = budgetResponse.data;
        const budgetArray = Array.isArray(budgetJson)
          ? budgetJson
          : (budgetJson.rows || []);


        setApiData(transactionArray);
        setBudgetData(budgetArray);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const salesReps = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i["Sales Rep"]).filter(Boolean)))];
  }, [apiData]);

  const saleTypes = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i.Type).filter(Boolean)))];
  }, [apiData]);

  const applyFilter = () => {
    setActiveStart(startPicker);
    setActiveEnd(endPicker);
    setActiveRep(repPicker);
    setActiveType(typePicker);
  };

  const filteredData = useMemo(() => {
    if (!apiData.length) return [];

    const s = new Date(activeStart), e = new Date(activeEnd);
    return apiData.filter(item => {
      const d = new Date(item.Date);
      const inDate = d >= s && d <= e;
      const repMatch = activeRep === "ALL" || item["Sales Rep"] === activeRep;
      const typeMatch = activeType === "ALL" || item.Type === activeType;
      return inDate && repMatch && typeMatch;
    });
  }, [apiData, activeStart, activeEnd, activeRep, activeType]);

  const categoryData = useMemo(() => {
    const months = getMonthsInRange(activeStart, activeEnd);

    // Build item to category mapping from transaction data
    const itemToCategoryMap = {};
    filteredData.forEach(item => {
      if (item.ITEM && item.Category) {
        itemToCategoryMap[item.ITEM] = item.Category;
      }
    });

    // Calculate revenue by category
    const categoryMap = {};
    filteredData.forEach(item => {
      const category = item.Category || "Unknown";
      const multiplier = typeMultipliers[item.Type] ?? 0;
      const revenue = parseFloat(item["Net Amount"]) || 0;

      if (!categoryMap[category]) {
        categoryMap[category] = {
          category,
          totalRevenue: 0,
          totalBudget: 0
        };
      }
      categoryMap[category].totalRevenue += revenue * multiplier;
    });

    // Add budget data by category
    budgetData.forEach(b => {
      // Extract month from budget date (assuming budget has a date field)
      const budgetDate = new Date(b.month || b.date);
      const budgetMonth = `${budgetDate.getFullYear()}-${String(budgetDate.getMonth() + 1).padStart(2, "0")}`;

      const isMonthMatch = months.includes(budgetMonth);
      const isRepMatch = activeRep === "ALL" || b.sales_rep === activeRep;
      if (!isMonthMatch || !isRepMatch) return;

      const item = b.item_name;
      const category = itemToCategoryMap[item];
      if (!category) return;

      if (!categoryMap[category]) {
        categoryMap[category] = {
          category,
          totalRevenue: 0,
          totalBudget: 0
        };
      }

      categoryMap[category].totalBudget += parseFloat(b.budgeted_amount) || 0;
    });

    return Object.values(categoryMap)
      .filter(cat => cat.totalRevenue !== 0 || cat.totalBudget !== 0)
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 7);
  }, [filteredData, budgetData, activeStart, activeEnd, activeRep]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalRevenue = categoryData.reduce((sum, cat) => sum + cat.totalRevenue, 0);
    const totalBudget = categoryData.reduce((sum, cat) => sum + cat.totalBudget, 0);
    const variance = totalRevenue - totalBudget;
    const variancePercentage = totalBudget > 0 ? ((variance / totalBudget) * 100) : 0;

    return {
      totalRevenue,
      totalBudget,
      variance,
      variancePercentage,
      categories: categoryData.length
    };
  }, [categoryData]);

  const formatTooltip = (value, name) => {
    return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, name];
  };

  const formatXAxisLabel = tickItem => {
    return tickItem.length > 15 ? tickItem.substring(0, 15) + "..." : tickItem;
  };

  const formatCurrency = (value) => {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Data...</h2>
          <p className="text-gray-600">Fetching transaction and budget data from server</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-3xl">⚠</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto px-6 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Budget Trends</h1>
              <p className="text-gray-600 text-lg">Visual analysis of budget vs revenue performance by category</p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Categories</p>
                <p className="text-2xl font-bold text-indigo-600">{summaryStats.categories}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Performance Variance</p>
                <p className={`text-2xl font-bold ${summaryStats.variancePercentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {summaryStats.variancePercentage >= 0 ? '+' : ''}{summaryStats.variancePercentage.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Filter Bar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Date Range */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startPicker}
                    onChange={e => setStartPicker(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endPicker}
                    onChange={e => setEndPicker(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Sales Rep */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Users className="inline h-4 w-4 mr-2" />
                  Sales Representative
                </label>
                <select
                  value={repPicker}
                  onChange={e => setRepPicker(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  {salesReps.map(rep => <option key={rep} value={rep}>{rep}</option>)}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="inline h-4 w-4 mr-2" />
                  Transaction Type
                </label>
                <select
                  value={typePicker}
                  onChange={e => setTypePicker(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  {saleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Apply Button */}
              <div className="flex items-end">
                <button
                  onClick={applyFilter}
                  className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  <RefreshCw size={18} />
                  <span>Update Chart</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summaryStats.totalRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Budget</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(summaryStats.totalBudget)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-3">
              <div className={`p-2 rounded-lg ${summaryStats.variance >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <BarChart3 className={`h-6 w-6 ${summaryStats.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Variance</p>
                <p className={`text-2xl font-bold ${summaryStats.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(summaryStats.variance)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <PieChart className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Categories</p>
                <p className="text-2xl font-bold text-purple-600">{summaryStats.categories}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Chart Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Category Performance Analysis</h2>
                <p className="text-indigo-100 text-sm">Comparing budget allocation vs actual revenue across top performing categories</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="h-[600px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={categoryData}
                  margin={{ top: 20, right: 30, left: 40, bottom: 80 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    opacity={0.7}
                  />
                  <XAxis
                    dataKey="category"
                    tick={{ fontSize: 12, fill: '#4b5563', fontWeight: 500 }}
                    tickFormatter={formatXAxisLabel}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#4b5563', fontWeight: 500 }}
                    tickFormatter={val => `$${(val / 1000).toFixed(0)}K`}
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <Tooltip
                    formatter={formatTooltip}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    labelStyle={{ color: '#374151', fontWeight: '600' }}
                  />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  />
                  <Bar
                    dataKey="totalBudget"
                    name="Total Budget"
                    fill="#3b82f6"
                    radius={[6, 6, 0, 0]}
                    opacity={0.8}
                  />
                  <Bar
                    dataKey="totalRevenue"
                    name="Total Revenue"
                    fill="#10b981"
                    radius={[6, 6, 0, 0]}
                    opacity={0.9}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart Insights */}
          <div className="border-t border-gray-100 bg-gray-50 px-8 py-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full opacity-80"></div>
                <span className="text-gray-600">
                  <span className="font-semibold text-blue-600">Budget</span> represents planned allocation
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-emerald-500 rounded-full opacity-90"></div>
                <span className="text-gray-600">
                  <span className="font-semibold text-emerald-600">Revenue</span> shows actual performance
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <span className="text-gray-600">
                  Showing top <span className="font-semibold">{categoryData.length}</span> performing categories
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;